import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import {
  CPA_PREVIEW_HREF,
  isCpaRole,
  readCpaPreviewFlag,
  searchHasCpaPreview,
  writeCpaPreviewFlag,
} from '../utils/cpaPortal';

/** Admin UI preview of the CPA shell, or a signed-in CPA user. */
export function useCpaPortalPreview() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isCpaUser = isCpaRole(user?.role);
  const isAdmin = user?.role === 'admin';
  const queryPreview = searchHasCpaPreview(location.search);

  useEffect(() => {
    if (!isAdmin) return;
    if (queryPreview) writeCpaPreviewFlag(true);
  }, [isAdmin, queryPreview]);

  const isPreview = isAdmin && (queryPreview || readCpaPreviewFlag());
  const isCpaShell = isCpaUser || isPreview;

  const enterPreview = useCallback(() => {
    writeCpaPreviewFlag(true);
    navigate(CPA_PREVIEW_HREF);
  }, [navigate]);

  const exitPreview = useCallback(() => {
    writeCpaPreviewFlag(false);
    const next = location.pathname.startsWith('/finances') ? location.pathname : '/finances';
    navigate(next);
  }, [location.pathname, navigate]);

  return useMemo(
    () => ({ isCpaUser, isPreview, isCpaShell, enterPreview, exitPreview }),
    [enterPreview, exitPreview, isCpaShell, isCpaUser, isPreview],
  );
}
